import { Box, Chip, Stack, Typography } from '@mui/material'
import { C1Button } from 'c1ui'
import { DeleteConfirmationDialogWithTextInput } from 'components/core/confirmation/dialog/delete'
import { C1CopyText } from 'components/core/copytext/C1CopyText'
import { ReadOnlyCodeViewer } from 'components/core/editor/ReadOnlyCodeViewer'
import { EmptyList } from 'components/core/empty-list'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { C1TableReduxHook } from 'components/core/list/C1Table/C1TableReduxHook'
import { PageHeader } from 'components/core/page-header'
import { useDrawer } from 'hooks/useDrawer'
import { Shield } from 'lucide-react'
import type { WorkloadFederationTrust } from 'pbts/c1/api/workload_federation/v1/workload_federation'
import {
    WorkloadFederationServiceDeleteTrustRequest,
    WorkloadFederationServiceListTrustsRequest,
    WorkloadFederationServiceUpdateTrustRequest,
} from 'pbts/c1/api/workload_federation/v1/workload_federation'
import { FieldMask } from 'pbts/google/protobuf/field_mask'
import { FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { deleteTrustApi, listTrustsApi, updateTrustApi } from 'redux_toolkit/workloadFederation'
import { TestTokenPanel } from './federation/TestTokenPanel'
import { SetupFederationWizard } from './SetupFederationWizard'

type SPFederationTabProps = {
    userId: string
}

const cols: C1TableReduxColumn<WorkloadFederationTrust>[] = [
    {
        field: 'displayName',
        label: <FormattedMessage id="wf_trust_col_name" defaultMessage="Trust" />,
        Component: ({ row }): React.ReactElement => (
            <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight={500}>{row.displayName}</Typography>
                    <Chip
                        label={row.disabled ? 'Disabled' : 'Enabled'}
                        color={row.disabled ? 'error' : 'success'}
                        size="small"
                    />
                </Stack>
            </Stack>
        ),
    },
    {
        field: 'details',
        label: <FormattedMessage id="wf_trust_col_details" defaultMessage="Configuration" />,
        Component: ({ row }): React.ReactElement => (
            <Stack spacing={1.5} sx={{ py: 0.5 }}>
                <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary">
                        <FormattedMessage id="wf_trust_detail_client_id" defaultMessage="Client ID" />
                    </Typography>
                    <C1CopyText value={row.clientId} typographyVariant="monospace" />
                </Stack>
                <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary">
                        <FormattedMessage id="wf_trust_detail_cel" defaultMessage="CEL condition" />
                    </Typography>
                    <ReadOnlyCodeViewer
                        value={row.conditionExpression}
                        language="cel"
                        maxHeight="150px"
                    />
                </Stack>
            </Stack>
        ),
    },
]

export const SPFederationTab: FC<SPFederationTabProps> = ({ userId }) => {
    const intl = useIntl()
    const [showDrawer, hideDrawer] = useDrawer()
    const { mutate: deleteTrust, inProgress: deleteInProgress } = deleteTrustApi()
    const { mutate: updateTrust } = updateTrustApi()
    const [deleteTarget, setDeleteTarget] = useState<WorkloadFederationTrust | null>(null)

    const request = useMemo(() => WorkloadFederationServiceListTrustsRequest.create({
        servicePrincipalId: userId,
    }), [userId])

    const handleSetupFederation = useCallback((): void => {
        showDrawer(
            <SetupFederationWizard close={hideDrawer} servicePrincipalId={userId} />,
            { variant: 'md' },
        )
    }, [showDrawer, hideDrawer, userId])

    const handleTestTrust = useCallback((trust: WorkloadFederationTrust): void => {
        showDrawer(
            <TestTokenPanel
                close={hideDrawer}
                servicePrincipalId={userId}
                clientId={trust.clientId}
                trustDisplayName={trust.displayName}
            />,
            { variant: 'sm' },
        )
    }, [showDrawer, hideDrawer, userId])

    const handleDelete = useCallback(async (): Promise<void> => {
        if (!deleteTarget || deleteInProgress) return
        await deleteTrust(WorkloadFederationServiceDeleteTrustRequest.create({
            servicePrincipalId: userId,
            clientId: deleteTarget.clientId,
        }))
        setDeleteTarget(null)
    }, [deleteTarget, deleteInProgress, deleteTrust, userId])

    const handleToggleDisabled = useCallback(async (trust: WorkloadFederationTrust): Promise<void> => {
        await updateTrust(WorkloadFederationServiceUpdateTrustRequest.create({
            servicePrincipalId: userId,
            trust: { ...trust, disabled: !trust.disabled },
            updateMask: FieldMask.create({ paths: ['disabled'] }),
        }))
    }, [updateTrust, userId])

    const actionsCol: C1TableReduxColumn<WorkloadFederationTrust> = useMemo(() => ({
        field: 'actions',
        label: '',
        Component: ({ row }): React.ReactElement => (
            <Stack direction="row" spacing={1}>
                <C1Button
                    variant="outlined"
                    onClick={(e: React.MouseEvent): void => {
                        e.stopPropagation()
                        handleTestTrust(row)
                    }}
                >
                    <FormattedMessage id="wf_trust_test" defaultMessage="Test" />
                </C1Button>
                <C1Button
                    variant="outlined"
                    onClick={(e: React.MouseEvent): void => {
                        e.stopPropagation()
                        handleToggleDisabled(row)
                    }}
                >
                    {row.disabled
                        ? <FormattedMessage id="wf_trust_enable" defaultMessage="Enable" />
                        : <FormattedMessage id="wf_trust_disable" defaultMessage="Disable" />
                    }
                </C1Button>
                <C1Button
                    variant="outlined"
                    color="error"
                    onClick={(e: React.MouseEvent): void => {
                        e.stopPropagation()
                        setDeleteTarget(row)
                    }}
                >
                    <FormattedMessage id="wf_trust_delete" defaultMessage="Delete" />
                </C1Button>
            </Stack>
        ),
    }), [handleToggleDisabled, handleTestTrust])

    const allCols = useMemo(() => [...cols, actionsCol], [actionsCol])

    return <>
        <PageHeader
            title={intl.formatMessage({ defaultMessage: 'Federation trusts', id: 'sp_federation_title' })}
            actions={
                <C1Button onClick={handleSetupFederation} variant="contained">
                    <FormattedMessage id="wf_setup_federation_button" defaultMessage="Setup federation" />
                </C1Button>
            }
        />
        <Box sx={{ mt: 2 }}>
            <C1TableReduxHook<WorkloadFederationServiceListTrustsRequest, WorkloadFederationTrust>
                cols={allCols}
                request={request}
                hook={listTrustsApi.useListTrusts}
                invalidateHook={listTrustsApi.useInvalidateListTrusts}
                hookFlags={{ notify: true, autoReload: true }}
                emptyState={
                    <EmptyList
                        title={
                            <FormattedMessage id="wf_trusts_empty" defaultMessage="No federation trusts" />
                        }
                        text={
                            <FormattedMessage id="wf_trusts_empty_text" defaultMessage="Setup federation to enable workload identity authentication for this service principal." />
                        }
                        icon={<C1Icon icon={Shield} />}
                    />
                }
            />
        </Box>
        {deleteTarget && (
            <DeleteConfirmationDialogWithTextInput
                open={!!deleteTarget}
                itemName={deleteTarget.displayName}
                details={
                    <FormattedMessage
                        id="wf_trust_delete_warning"
                        defaultMessage="Deleting this trust will immediately prevent any workloads using its client ID from authenticating. This action cannot be undone."
                    />
                }
                onConfirm={handleDelete}
                onCancel={(): void => setDeleteTarget(null)}
                isActionLoading={deleteInProgress}
            />
        )}
    </>
}
